import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
    deleteSmsRunRating,
    deleteVoiceCallRating,
    saveRunFeedback,
    saveSmsRunRating,
    saveVoiceCallRating,
} from '@apps/web/components/phoebe_chat_view/conversation/chat_backend_adapter';
import { useTeachPhoebeAnalytics } from '@apps/web/features/teach_phoebe/model/use_teach_phoebe_analytics';
import { type TrainingRunRead } from '@libraries/typescript/api_client';

type TrainingRunRef = Pick<
    TrainingRunRead,
    'id' | 'channel' | 'sms_run_id' | 'run_type' | 'outreach_id'
>;

// Semantic commands for a training run row, shared by the Teach Phoebe page
// and the home training card so both dispatch chat vs voice identically.
// Presentational rows own their own loading, toast, and pin state and call
// these commands; the channel-to-endpoint mapping lives here, not in the UI.
export function useTrainingRunActions(organizationId: string | null) {
    const navigate = useNavigate();
    const { captureRunOpened, captureRunReviewed } =
        useTeachPhoebeAnalytics(organizationId);

    const openRun = useCallback(
        (run: TrainingRunRef): 'navigated' | 'chat-transcript' => {
            if (run.channel === 'voice') {
                // Voice rows open the call detail (transcript + recording);
                // the row id is the VoiceCall id. /voice-calls is a
                // coordinator page.
                captureRunOpened({ channel: 'voice' });
                void navigate({
                    to: '/voice-calls',
                    search: { voiceCallId: run.id },
                });
                return 'navigated';
            }
            if (run.channel === 'sms') {
                // The SMS row id is the conversation id; open the thread where
                // the coordinator reviews each Phoebe SMS run.
                captureRunOpened({ channel: 'sms' });
                void navigate({
                    to: '/conversations',
                    search: { conversationId: run.id },
                });
                return 'navigated';
            }
            if (run.run_type === 'outreach_analysis' && run.outreach_id) {
                captureRunOpened({ channel: 'chat' });
                void navigate({
                    to: '/outreaches/$callout-id',
                    params: { 'callout-id': run.outreach_id },
                });
                return 'navigated';
            }
            // Other chat runs open read-only in the row's transcript dialog —
            // never the docked general chat, which is writable and persists
            // its selected session.
            captureRunOpened({ channel: 'chat' });
            return 'chat-transcript';
        },
        [captureRunOpened, navigate],
    );

    const saveReview = useCallback(
        async ({
            run,
            label,
            comment,
        }: {
            readonly run: TrainingRunRef;
            readonly label: 'good' | 'bad';
            readonly comment?: string;
        }) => {
            if (!organizationId) {
                return;
            }
            if (run.channel === 'voice') {
                // Voice feedback writes the call's own rating (its single
                // home); in a training org the comment also trains Phoebe.
                await saveVoiceCallRating({
                    organizationId,
                    voiceCallId: run.id,
                    label,
                    comment,
                });
                captureRunReviewed({
                    channel: 'voice',
                    label,
                    hasComment: Boolean(comment),
                });
                return;
            }
            if (run.channel === 'sms') {
                // The feed thumb rates the conversation's latest Phoebe run
                // (a run = one inbound); per-run review lives in the opened
                // thread. A missing run means an empty conversation slipped
                // into the feed — nothing to rate.
                if (!run.sms_run_id) {
                    return;
                }
                await saveSmsRunRating({
                    organizationId,
                    runId: run.sms_run_id,
                    label,
                    comment,
                });
                captureRunReviewed({
                    channel: 'sms',
                    label,
                    hasComment: Boolean(comment),
                });
                return;
            }
            // Response counts are only known inside the chat view; omitting
            // the count preserves any value recorded there.
            await saveRunFeedback({
                organizationId,
                runId: run.id,
                status: 'submitted',
                label,
                comment,
            });
            captureRunReviewed({
                channel: 'chat',
                label,
                hasComment: Boolean(comment),
            });
        },
        [captureRunReviewed, organizationId],
    );

    // Clear a review (unclick): people change their mind. Voice and SMS drop
    // the rating outright; chat has no delete, so dismissing clears the
    // good/bad label and the feed thumb un-highlights.
    const clearReview = useCallback(
        async (run: TrainingRunRef) => {
            if (!organizationId) {
                return;
            }
            if (run.channel === 'voice') {
                await deleteVoiceCallRating({
                    organizationId,
                    voiceCallId: run.id,
                });
                captureRunReviewed({
                    channel: 'voice',
                    label: 'cleared',
                    hasComment: false,
                });
                return;
            }
            if (run.channel === 'sms') {
                if (!run.sms_run_id) {
                    return;
                }
                await deleteSmsRunRating({
                    organizationId,
                    runId: run.sms_run_id,
                });
                captureRunReviewed({
                    channel: 'sms',
                    label: 'cleared',
                    hasComment: false,
                });
                return;
            }
            await saveRunFeedback({
                organizationId,
                runId: run.id,
                status: 'dismissed',
            });
            captureRunReviewed({
                channel: 'chat',
                label: 'cleared',
                hasComment: false,
            });
        },
        [captureRunReviewed, organizationId],
    );

    return { openRun, saveReview, clearReview };
}
